import type { ApiErrorType } from "./apiErrorType";

const POST_LOGIN_REDIRECT_KEY = "redirectPath";
const POST_LOGIN_RESTORE_STATE_KEY = "postLoginRestore";

export const POST_LOGIN_REDIRECT_TTL_MS = 30 * 60 * 1000;

interface StoredPostLoginRedirect {
  version: 1;
  path: string;
  expiresAt: number;
}

interface PostLoginRestoreFallback {
  message: string;
}

const SUPPORTED_PATH_PATTERNS: RegExp[] = [
  /^\/$/,
  /^\/home\/?$/,
  /^\/properties\/?$/,
  /^\/properties\/(?:create|search)\/?$/,
  /^\/properties\/[^/]+\/?$/,
  /^\/properties\/[^/]+\/units\/[^/]+\/?$/,
  /^\/properties\/[^/]+\/private-area\/(?:create|[^/]+)\/?$/,
  /^\/budget-analysis\/?$/,
  /^\/budget-analysis\/group\/[^/]+\/?$/,
  /^\/budget-analysis-sample\/?$/,
  /^\/repairs\/(?:new|[^/]+)\/?$/,
  /^\/expenses\/(?:new|[^/]+)\/?$/,
  /^\/payments\/[^/]+\/?$/,
  /^\/management-tasks\/[^/]+\/?$/,
  /^\/management-task-results\/[^/]+\/?$/,
  /^\/estimates(?:\/[^/]+)?\/?$/,
  /^\/setting\/accout\/[^/]+\/?$/,
  /^\/setting\/(?:system|custom|custom-form|fiscal-closing)\/?$/,
  /^\/ai-sample\/?$/,
];

const INVALID_PATH_SEGMENTS = new Set(["undefined", "null"]);

const getSessionStorage = (): Storage | null => {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
};

const hasInvalidPathSegment = (pathname: string): boolean =>
  pathname
    .split("/")
    .filter(Boolean)
    .some((segment) => INVALID_PATH_SEGMENTS.has(segment.toLowerCase()));

export const isSupportedPostLoginRedirect = (path: string): boolean => {
  if (!path.startsWith("/") || path.startsWith("//")) return false;

  const pathname = path.split(/[?#]/, 1)[0];
  if (!pathname || hasInvalidPathSegment(pathname)) return false;

  return SUPPORTED_PATH_PATTERNS.some((pattern) => pattern.test(pathname));
};

export const savePostLoginRedirect = (
  path: string,
  savedAt = Date.now(),
): void => {
  const storage = getSessionStorage();
  if (!storage || !isSupportedPostLoginRedirect(path)) return;

  const redirect: StoredPostLoginRedirect = {
    version: 1,
    path,
    expiresAt: savedAt + POST_LOGIN_REDIRECT_TTL_MS,
  };
  try {
    storage.setItem(POST_LOGIN_REDIRECT_KEY, JSON.stringify(redirect));
  } catch {
    // Storage が利用できない環境では復帰せず、通常のホーム遷移へ委ねる。
  }
};

export const clearPostLoginRedirect = (): void => {
  try {
    getSessionStorage()?.removeItem(POST_LOGIN_REDIRECT_KEY);
  } catch {
    // Storage が利用できない場合もログアウト処理は継続する。
  }
};

export const consumePostLoginRedirect = (now = Date.now()): string | null => {
  const storage = getSessionStorage();
  if (!storage) return null;

  let storedValue: string | null = null;
  try {
    storedValue = storage.getItem(POST_LOGIN_REDIRECT_KEY);
    storage.removeItem(POST_LOGIN_REDIRECT_KEY);
  } catch {
    return null;
  }
  if (!storedValue) return null;

  try {
    const redirect = JSON.parse(
      storedValue,
    ) as Partial<StoredPostLoginRedirect>;
    if (
      redirect.version !== 1 ||
      typeof redirect.path !== "string" ||
      typeof redirect.expiresAt !== "number" ||
      redirect.expiresAt <= now ||
      !isSupportedPostLoginRedirect(redirect.path)
    ) {
      return null;
    }
    return redirect.path;
  } catch {
    return null;
  }
};

export const createPostLoginRestoreState = (): Record<string, boolean> => ({
  [POST_LOGIN_RESTORE_STATE_KEY]: true,
});

export const getPostLoginRestoreFallback = (
  errorType: ApiErrorType | null | undefined,
  locationState: unknown,
): PostLoginRestoreFallback | null => {
  const isRestore =
    Boolean(locationState) &&
    typeof locationState === "object" &&
    (locationState as Record<string, unknown>)[POST_LOGIN_RESTORE_STATE_KEY] ===
      true;
  if (!isRestore) return null;

  if (errorType === "notFound") {
    return {
      message: "前回のページを開けなかったため、ホームへ移動しました。",
    };
  }
  if (errorType === "forbidden") {
    return {
      message: "前回のページを表示する権限がないため、ホームへ移動しました。",
    };
  }
  return null;
};
